/**
 * Round timer source of truth for the UI (Phase 1.1).
 *
 * After the Phase 1.1 cut the diamond routes TimerViewFacet.getTimerInfo(), which returns the
 * EFFECTIVE deadline: the contract's own LibRound.effectiveDeadline(), the same value
 * endRoundIfExpired(), the buy lock and the keeper use. An empty round (0 keys sold) ends
 * EMPTY_ROUND_DURATION (15,540s) after it starts; once a key is sold it is the stored deadline.
 *
 * Before the cut getTimerInfo() is not routed (the diamond reverts), and we fall back to
 * getRoundInfo() exactly as before. Dependency-free so the Base Sepolia fork test can import it.
 */
export const TIMER_INFO_ABI = [
  'function getTimerInfo() view returns (uint256 roundId, uint256 roundStart, uint256 rawDeadline, uint256 effectiveDeadline, uint256 timeRemaining, uint256 keysSold, bool fastClock, bool expired, uint256 emptyRoundDuration)',
];

export interface RoundTimer {
  source: 'timerInfo' | 'roundInfo';
  /** unix seconds; count down to this */
  deadline: number;
  rawDeadline: number;
  timeRemaining: number;
  keysSold: number;
  /** empty round and the fast clock decides the end time */
  fastClock: boolean;
  /** no keys sold AND the contract enforces the empty-round rule (Reaper keys blocked) */
  emptyRound: boolean;
  emptyRoundDuration: number;
}

type RoundInfoLike = { deadline?: unknown; timeRemaining?: unknown; totalKeysSold?: unknown };
type TimerInfoLike = {
  rawDeadline: unknown; effectiveDeadline: unknown; timeRemaining: unknown; keysSold: unknown;
  fastClock: unknown; emptyRoundDuration: unknown;
};

const n = (x: unknown) => Number(x ?? 0);

// After a "not routed" answer, don't ask again for a while (one cheap probe every 5 min).
const RETRY_MS = 5 * 60_000;
let unsupportedUntil = 0;

/** Calls getTimerInfo() if the diamond routes it; null when it does not (or the call failed). */
export async function fetchTimerInfo(call: () => Promise<TimerInfoLike>): Promise<TimerInfoLike | null> {
  if (Date.now() < unsupportedUntil) return null;
  try {
    return await call();
  } catch (e: any) {
    // a revert (selector not routed) is deterministic; transport errors just fall back this once
    if (e?.code === 'CALL_EXCEPTION' || /revert|does not exist/i.test(String(e?.message ?? ''))) unsupportedUntil = Date.now() + RETRY_MS;
    return null;
  }
}

/** Tests only: forget a cached "not routed" answer. */
export function resetTimerInfoProbe() { unsupportedUntil = 0; }

export function toRoundTimer(timer: TimerInfoLike | null, round: RoundInfoLike): RoundTimer {
  if (timer) {
    const keys = n(timer.keysSold);
    return {
      source: 'timerInfo',
      deadline: n(timer.effectiveDeadline),
      rawDeadline: n(timer.rawDeadline),
      timeRemaining: n(timer.timeRemaining),
      keysSold: keys,
      fastClock: Boolean(timer.fastClock),
      emptyRound: keys === 0,
      emptyRoundDuration: n(timer.emptyRoundDuration),
    };
  }
  return {
    source: 'roundInfo',
    deadline: n(round.deadline),
    rawDeadline: n(round.deadline),
    timeRemaining: n(round.timeRemaining),
    keysSold: n(round.totalKeysSold),
    fastClock: false,
    emptyRound: false,
    emptyRoundDuration: 0,
  };
}
